import { X } from 'lucide-react'
import { hintergrundSchliesst } from './ui/hintergrund'
import { Button } from './ui/primitives'

/**
 * **Die Anleitung für die eigene Microsoft-Verbindung – in Work, nicht in einer Datei**
 * (Nils, 15.09.2026: *„Die Anleitung muss in Work. Wie starte ich? Wie kann ich einbinden?"*).
 *
 * Ein Fenster aus der Seitenleiste, für jede Person, in ihrer Sprache: was passiert beim Klick,
 * was Work danach liest, wie eine Mail oder eine To-Do-Aufgabe zur Aufgabe wird, was
 * „erledigt" in beide Richtungen tut, und wie man wieder herauskommt. Der Entra-Teil für die
 * Administration steht **nicht** hier, sondern in KI & Einstellungen (`MicrosoftEinrichtung`) –
 * eine Entwicklerin soll nicht lesen müssen, was nur Nils klicken kann.
 */
export function MicrosoftAnleitung({
  schliessen,
  verbunden,
  eingerichtet,
  todo,
}: {
  schliessen: () => void
  verbunden: boolean
  eingerichtet: boolean
  todo: boolean
}) {
  return (
    <div
      className="fixed inset-x-0 bottom-0 unter-leiste z-[60] flex items-start justify-center bg-black/35 p-2 pt-[5vh] sm:p-6 sm:pt-[10vh]"
      {...hintergrundSchliesst(schliessen)}
      onKeyDown={(e) => {
        if (e.key === 'Escape') schliessen()
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Anleitung Microsoft 365"
    >
      <div className="flex max-h-[min(90dvh,100%)] w-full min-w-0 max-w-[560px] flex-col overflow-y-auto rounded-card border border-line bg-surface p-[18px] shadow-card text-[13px] leading-relaxed">
        <div className="flex items-start gap-2">
          <h3 className="flex-1 text-[16px] font-bold">Outlook und To Do mit Work verbinden</h3>
          <button
            type="button"
            onClick={schliessen}
            aria-label="Schließen"
            className="tap -mt-1 -mr-2 cursor-pointer rounded-lg p-2 text-muted hover:bg-[#f3f2ef] hover:text-ink"
          >
            <X className="size-[20px]" />
          </button>
        </div>

        <p className="mt-2 text-ink2">
          Du verbindest <b>dein eigenes</b> Microsoft-365-Konto. Work bekommt dafür einen Zugang,
          der nur das darf, was du darfst, und nur in deinem Namen. Niemand sonst sieht deine Mails
          oder To-Dos; sie landen in deiner persönlichen Arbeitswelt.
        </p>

        {!eingerichtet && (
          <p className="mt-3 rounded-lg bg-[#fdf3dc] px-3 py-2 text-[12.5px] text-[#8a6100]">
            Der Microsoft-Zugang ist in Work noch nicht eingerichtet. Das macht die Administration
            unter <b>KI &amp; Einstellungen → Zugänge</b>; bis dahin lässt sich nichts verbinden.
          </p>
        )}

        <Schritt nr={1} titel="Verbinden">
          Unten in der Seitenleiste auf <b>„Mit Microsoft 365 verbinden"</b> klicken. Microsoft
          öffnet sich, du wählst dein Konto und stimmst einmal zu. Danach steht dein Konto in der
          Seitenleiste, und der erste Abgleich läuft sofort.
          {verbunden && <span className="text-good-text"> Das ist bei dir schon geschehen.</span>}
        </Schritt>

        <Schritt nr={2} titel="Persönliche Arbeitswelt">
          Die Aufgaben landen in deiner <b>persönlichen Arbeitswelt</b> (z. B. „Meine ToDos"). Hast
          du keine, lege sie an: Seitenleiste → Arbeitswelten → persönliche anlegen. Die
          Seitenleiste sagt dir, wenn sie fehlt.
        </Schritt>

        <Schritt nr={3} titel="Mails aus Outlook">
          In Outlook eine Mail <b>kennzeichnen</b> (die Fahne). Du darfst sie danach archivieren
          oder in einen Ordner sortieren – Work findet sie trotzdem. Binnen fünf Minuten (oder nach
          „Jetzt abgleichen") steht sie im Vorlauf von „Mein Tag" mit der Marke <b>Outlook</b>:
          Betreff als Titel, Text als Beschreibung, „Quelle öffnen" führt zur Mail.
        </Schritt>

        <Schritt nr={4} titel="Aufgaben aus To Do">
          {todo || !verbunden ? (
            <>
              In der <b>To-Do-App</b> (oder per Siri, wenn Erinnerungen mit Outlook verbunden sind)
              eine Aufgabe anlegen – sie erscheint mit der Marke <b>To Do</b>. „Wichtig" wird Prio
              A, das Datum wird die Fälligkeit, die Liste steht in der Beschreibung. Die Liste
              „Gekennzeichnete E-Mails" übergeht Work, sonst stünde jede Mail zweimal da.
            </>
          ) : (
            <>
              Deine Verbindung ist von vor der To-Do-Anbindung. Einmal <b>neu verbinden</b>{' '}
              (Seitenleiste), dann kommen die Aufgaben aus der To-Do-App mit.
            </>
          )}
        </Schritt>

        <Schritt nr={5} titel="Erledigen – in beide Richtungen">
          Setzt du die Aufgabe in Work auf <b>Erledigt</b>, wird in Outlook aus der Fahne ein
          Häkchen bzw. die To-Do-Aufgabe abgehakt – binnen Sekunden. Hakst du in Outlook oder To Do
          ab, ist die Aufgabe in Work erledigt. Entfernst du die Kennzeichnung oder löschst die
          To-Do-Aufgabe, wird sie in Work <b>zurückgestellt</b> – „war doch keine Aufgabe", nicht
          „fertig".
        </Schritt>

        <Schritt nr={6} titel="Termine">
          Deine <b>heutigen Termine</b> stehen als Zeile über „Mein Tag". Work liest den Kalender
          nur, es schreibt nichts hinein.
        </Schritt>

        <Schritt nr={7} titel="Was Work liest – und wie du wieder rauskommst">
          Nur <b>gekennzeichnete</b> Mails (Betreff, Absender, Text), deine To-Do-Aufgaben und die
          Termine von heute. Ungekennzeichnete Mails liest Work nie. Mit <b>„Trennen"</b> in der
          Seitenleiste löscht Work den Zugang; vorhandene Aufgaben bleiben. Läuft der Zugang nach 90
          Tagen ohne Nutzung ab, steht „Neu verbinden" da – ein Klick.
        </Schritt>

        <div className="mt-4 flex justify-end">
          <Button variant="primary" onClick={schliessen}>
            Verstanden
          </Button>
        </div>
      </div>
    </div>
  )
}

function Schritt({
  nr,
  titel,
  children,
}: {
  nr: number
  titel: string
  children: React.ReactNode
}) {
  return (
    <div className="mt-3 flex gap-2.5">
      <span className="tabular mt-px flex size-5 shrink-0 items-center justify-center rounded-full bg-accent-soft text-[11px] font-bold text-accent-deep">
        {nr}
      </span>
      <div className="min-w-0">
        <div className="font-semibold">{titel}</div>
        <p className="text-ink2">{children}</p>
      </div>
    </div>
  )
}
